import { Injectable } from '@angular/core';
import { CountriesService } from '@core/services/countries.service';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { SpinnerService } from '@core/services/spinner.service';
import { CyclesAPI } from '@core/typings/api/cycles.typing';
import { VettingRequestStatusId } from '@core/typings/organization.typing';
import { ProcessingTypes } from '@core/typings/payment.typing';
import { CanOrgSubmitMultipleAppsOptions, ProgramDetail } from '@core/typings/program.typing';
import { AddOrgUI } from '@core/typings/ui/add-org.typing';
import { AddOrganizationService } from '@features/add-organization/add-organization.service';
import { ApplicationApplicantService } from '@features/apply/application-applicant.service';
import { CyclesService } from '@features/cycles/cycles.service';
import { NonprofitService } from '@features/nonprofit/nonprofit.service';
import { ProgramService } from '@features/programs/services/program.service';
import { OrganizationEligibleForGivingStatus, SearchResult } from '@yourcause/common';
import { I18nService } from '@yourcause/common/i18n';
import { ConfirmationModalComponent, ModalFactory } from '@yourcause/common/modals';
import { ApplicationEligibilityResources } from './application-eligibility.resources';
import { ApplicationEligibility } from './application-eligibility.typing';

@Injectable({
  providedIn: 'root'
})
export class ApplicationEligibilityService {

  constructor (
    private applicationEligibilityResources: ApplicationEligibilityResources,
    private i18n: I18nService,
    private modalFactory: ModalFactory,
    private applicationApplicantService: ApplicationApplicantService,
    private programService: ProgramService,
    private nonprofitService: NonprofitService,
    private cyclesService: CyclesService,
    private portal: PortalDeterminationService,
    private spinnerService: SpinnerService,
    private addOrganizationService: AddOrganizationService,
    private countriesService: CountriesService
  ) { }

  getOrganizationNotEligibleText () {
    return this.i18n.translate(
      'common:hdrOrganizationNotEligible',
      {},
      'Organization Not Eligible'
    );
  }

  getUnableToUpdateApplicationText () {
    return this.i18n.translate(
      'PROGRAM:hdrUnableToUpdateApplication',
      {},
      'Unable to Update Application'
    );
  }

  getUnableToCopyApplicationText () {
    return this.i18n.translate(
      'PROGRAM:hdrUnableToCopyApplication',
      {},
      'Unable to Copy Application'
    );
  }

  /**
   * Determines based on the payload and scenario if you can proceed
   *
   * @param payload: Eligibility Info
   * @param scenario: Scenario for Check
   * @param isNomination: is nomination?
   * @param multipleApplicationsLimitType: program setting to limit apps
   * @param multipleApplicationsLimitMessage: program setting message to limit apps
   * @param organizationId: organization ID
   * @returns if the application eligibility scenario can proceed
   */
  async handleApplicationEligibilityScenario (
    payload: ApplicationEligibility.ProgramUpdateEligibilityPayload,
    scenario: ApplicationEligibility.ApplicationEligibilityScenario,
    isNomination: boolean,
    multipleApplicationsLimitType?: CanOrgSubmitMultipleAppsOptions, // only evaluated for applicants
    multipleApplicationsLimitMessage?: string, // only evaluated for applicants
    organizationId?: number // only evaluated for applicants
  ) {
    const response = await this.checkIfScenarioCanProceed(payload);
    let isValid = response.isValid;
    if (isValid && this.portal.isApply) {
      isValid = await this.handleOrgEligibilityForMultipleApps(
        payload.grantProgramId,
        payload.grantProgramCycleId,
        organizationId,
        multipleApplicationsLimitType
      );
    }
    if (!isValid) {
      const errorMessage = this.getMessageForIneligibleApplication(
        response.eligibilityCode,
        payload.latestVettingRequestStatusForOrg,
        scenario,
        isNomination,
        multipleApplicationsLimitType,
        multipleApplicationsLimitMessage
      );
      await this.showEligibilityError(
        errorMessage,
        this.getModalHeaderByScenarioType(scenario)
      );

      return false;
    }

    return true;
  }

  /**
   * Determine if we are allowed to proceed with the org/program/cycle combo
   *
   * @param programId: Program ID
   * @param cycleId: Cycle ID
   * @param orgId: Org ID
   * @param multipleApplicationsLimitType: Program Setting
   * @returns if we are allowed to proceed
   */
  async handleOrgEligibilityForMultipleApps (
    programId: number,
    cycleId: number,
    orgId: number,
    multipleApplicationsLimitType: CanOrgSubmitMultipleAppsOptions
  ): Promise<boolean> {
    if (!programId || multipleApplicationsLimitType === CanOrgSubmitMultipleAppsOptions.Yes) {
      return true;
    }

    const result = await this.applicationEligibilityResources.doesOrgPassProgramEligibility(
      orgId,
      cycleId,
      programId
    );

    return result.mayApply;
  }

  /**
   * Checks if we can proceed with scenario
   *
   * @param payload: Eligibility Info
   * @returns if the scenario can proceed
   */
  async checkIfScenarioCanProceed (
    payload: ApplicationEligibility.ProgramUpdateEligibilityPayload
  ): Promise<{
    isValid: boolean;
    eligibilityCode: ApplicationEligibility.OrganizationEligibilityCheckResponse;
  }> {
    if (payload.orgIdentification) {
      const eligibilityCode = await this.getOrgEligibilityForProgram(payload);
      const isValid = this.getCanProceedByEligibilityCode(eligibilityCode);

      return {
        isValid,
        eligibilityCode
      };
    }

    return {
      isValid: true,
      eligibilityCode: null
    };
  }

  /**
   * Determines whehter you can proceed with the eligibility code
   *
   * @param eligibilityCode: Eligiblity Code
   * @returns if you can proceed
   */
  getCanProceedByEligibilityCode (
    eligibilityCode: ApplicationEligibility.OrganizationEligibilityCheckResponse
  ) {
    if (eligibilityCode !== ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationEligible) {
      if (eligibilityCode === ApplicationEligibility.OrganizationEligibilityCheckResponse.VettingRequestAlreadyExists) {
        return true;
      } else {
        return false;
      }
    }

    return true;
  }

  /**
   * Determines if the org is valid for the given program
   *
   * @param payload: Eligibility info
   * @returns if the org is valid for the program
   */
  async getOrgEligibilityForProgram (
    payload: ApplicationEligibility.ProgramUpdateEligibilityPayload
  ) {
    let apiResponse: ApplicationEligibility.OrganizationEligibilityCheckResponse;
    const forApplicant = this.portal.isApply;
    if (forApplicant) {
      apiResponse = await this.applicationEligibilityResources.checkOrganizationEligibilityStatus(payload);
    } else {
      apiResponse = await this.applicationEligibilityResources.organizationEligibilityCheck(payload);
    }
    let charityBucketEligible = true;
    if (!!payload.charityBucketId) {
      charityBucketEligible = await this.isOrgValidForCharityBucket(
        payload.orgIdentification,
        payload.charityBucketId,
        payload.clientId
      );
    }

    return charityBucketEligible ?
      apiResponse :
      ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationNotInCharityBucket;
  }

  /**
   * Returns the modal header for the given scenario
   *
   * @param scenario: Scenario type
   * @returns the modal header
   */
  getModalHeaderByScenarioType (
    scenario: ApplicationEligibility.ApplicationEligibilityScenario
  ) {
    switch (scenario) {
      case ApplicationEligibility.ApplicationEligibilityScenario.CopyApplication:
        return this.getUnableToCopyApplicationText();
      case ApplicationEligibility.ApplicationEligibilityScenario.OrgForProgramRouting:
        return this.getOrganizationNotEligibleText();
      case ApplicationEligibility.ApplicationEligibilityScenario.UpdateCycle:
        return this.getUnableToUpdateApplicationText();
    }
  }

  /**
   * Returns the message describing why the action is not allowed
   *
   * @param eligibilityCode: The eligibility code
   * @param latestVettingRequestStatusForOrg: The latest vetting status from the org
   * @param scenarioType: the scenario we are checking
   * @param isNomination: Is nomination?
   * @param multipleApplicationsLimitType: the program setting for limiting submitted apps
   * @param multipleApplicationsLimitMessage: the program message for limiting submitted apps
   * @returns the message that says why it's ineligible
   */
  getMessageForIneligibleApplication (
    eligibilityCode: ApplicationEligibility.OrganizationEligibilityCheckResponse,
    latestVettingRequestStatusForOrg: VettingRequestStatusId,
    scenarioType: ApplicationEligibility.ApplicationEligibilityScenario,
    isNomination: boolean,
    multipleApplicationsLimitType?: CanOrgSubmitMultipleAppsOptions,
    multipleApplicationsLimitMessage?: string
  ): string {
    let errorMessage = '';
    const isEligible = !eligibilityCode ||
      eligibilityCode === ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationEligible;
    if (isEligible && multipleApplicationsLimitType) {
      return this.getOrgEligibilityMessageforMultipleApps(
        multipleApplicationsLimitType,
        multipleApplicationsLimitMessage,
        isNomination
      );
    }
    switch (eligibilityCode) {
      default:
      case ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationEligible:
        errorMessage = null;
        break;
      case ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationLocationNotValid:
        errorMessage = this.i18n.translate(
          'common:textOrgNotEligibleForProgram',
          {},
          `The selected organization is not eligible for the program at this time`
        );
        break;
      case ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationNotActiveInNpp:
        switch (scenarioType) {
          case ApplicationEligibility.ApplicationEligibilityScenario.CopyApplication:
            errorMessage = this.i18n.translate(
              'PROGRAM:textOrganizationNoLongerActiveInNPP',
              {},
              `This application cannot be copied or moved. The organization linked to this application is no longer active in YourCause + Blackbaud's Global Good Network and cannot accept applications.`
            );
            break;
          case ApplicationEligibility.ApplicationEligibilityScenario.UpdateCycle:
          default:
            errorMessage = this.i18n.translate(
              'PROGRAM:textOrganizationNoLongerActiveInNPPAppUpdate',
              {},
              `The application cannot be updated. The organization linked to this application is no longer active in YourCause + Blackbaud's Global Good Network and cannot accept applications.`
            );
            break;
        }
        break;
      case ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationNotValidForRegAuth:
      case ApplicationEligibility.OrganizationEligibilityCheckResponse.VettingRequestAlreadyExists:
        if (!latestVettingRequestStatusForOrg) {
          switch (scenarioType) {
            case ApplicationEligibility.ApplicationEligibilityScenario.CopyApplication:
              errorMessage = this.i18n.translate(
                'PROGRAM:textOrganizationNotEligibleForGiving2',
                {},
                'This application cannot be copied or moved. The organization linked to this application is not currently eligible for giving.'
              );
              break;
            case ApplicationEligibility.ApplicationEligibilityScenario.UpdateCycle:
            case ApplicationEligibility.ApplicationEligibilityScenario.OrgForProgramRouting:
              errorMessage = this.i18n.translate(
                'PROGRAM:textOrganizationNotEligibleGivingRouting',
                {},
                `The organization you selected is not eligible for giving in the YourCause network. Based on this program's guidelines, applications cannot be submitted on behalf of this organization.`
              );
              break;
          }
        }
        break;
      case ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationNotValidForGrantProgramBudget:
        // Only scenario for this is Update Cycle - add switch case here if we eventually have more scenarios
        errorMessage = this.i18n.translate(
          'PROGRAM:textOrganizationNotValidForBudget2',
          {},
          'This application cannot be updated. The organization linked to this application is not valid for the selected budget at this time.'
        );
        break;
      case ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationNotInCharityBucket:
        switch (scenarioType) {
          case ApplicationEligibility.ApplicationEligibilityScenario.CopyApplication:
            errorMessage = this.i18n.translate(
              'PROGRAM:textOrganizationNotInCharityBucket2',
              {},
              'This application cannot be copied or moved. This organization is no longer included in the preferred charity list for this program.'
            );
            break;
          case ApplicationEligibility.ApplicationEligibilityScenario.OrgForProgramRouting:
          case ApplicationEligibility.ApplicationEligibilityScenario.UpdateCycle:
            errorMessage = this.i18n.translate(
              'common:textOrganizationNotInCharityBucketRouting',
              {},
              `The organization you selected is not included in the charities approved for this program. Based on this program's guidelines, applications cannot be submitted on behalf of this organization.`
            );
            break;
        }
    }

    return errorMessage;
  }

  /**
   * Opens a modal that shows the user the reason they can't proceed
   *
   * @param errorMessage: Error message to show
   * @param modalHeader: Modal Header
   */
  async showEligibilityError (
    errorMessage: string,
    modalHeader: string
  ) {
    this.spinnerService.stopSpinner();
    await this.modalFactory.open(
      ConfirmationModalComponent,
      {
        confirmText: errorMessage,
        modalHeader,
        confirmButtonText: this.i18n.translate('common:textClose', {}, 'Close')
      }
    );
  }

  /**
   * Determine whether the application can be copied
   *
   * @param applicationId: Application ID
   * @param grantProgramId: Program ID
   * @param isNomination: Is nomination?
   * @param organizationId: Organization ID
   * @returns if copy application can proceed
   */
  async checkCopyEligibilityForApplicantView (
    applicationId: number,
    grantProgramId: number,
    isNomination: boolean,
    organizationId: number
  ): Promise<boolean> {
    const program = await this.programService.handleGetProgramForApplicant('' + grantProgramId);
    const copyApplication = await this.applicationApplicantService.getApplicationByForm(
      applicationId,
      program.form.formId
    );
    if (!!copyApplication) {
      const cyclesForDisplay = this.cyclesService.getCycleDateHelpers(program.cycles);
      const currentCycle = cyclesForDisplay.currentCycle;
      const payload: ApplicationEligibility.ProgramUpdateEligibilityPayload = {
        applicationId,
        grantProgramCycleId: currentCycle?.id ?? copyApplication.cycle.id,
        grantProgramId: program.grantProgramId,
        orgIdentification: copyApplication.organization?.orgIdentification,
        charityBucketId: program.charityBucketId,
        clientId: program.clientId,
        latestVettingRequestStatusForOrg: copyApplication.latestVettingRequestStatusForOrg
      };

      return this.handleApplicationEligibilityScenario(
        payload,
        ApplicationEligibility.ApplicationEligibilityScenario.CopyApplication,
        isNomination,
        program.multipleApplicationsLimitType,
        program.multipleApplicationsLimitMessage,
        organizationId
      );
    }

    return false;
  }

  /**
   * Checkes whether a given org can apply to a program and it's current cycle
   *
   * @param selectedOrg: Selected Org
   * @param grantProgramId: Program ID
   * @param isNomination: Is nomination?
   * @returns if the selected org can apply to the program
   */
  async ensureOrgIsValidForProgramRouting (
    selectedOrg: SearchResult,
    grantProgramId: number,
    isNomination: boolean
  ): Promise<{
    isValidOrg: boolean;
    errorMessage: string;
  }> {
    const program = await this.programService.handleGetProgramForApplicant(grantProgramId);
    const { currentCycle } = this.cyclesService.getCycleDateHelpers(program.cycles);
    const response = await this.isProgramRoutingOrgEligible(selectedOrg, program, currentCycle);
    const eligibilityCode = response.eligibilityCode;
    let isValidOrg = response.isValidOrg;
    if (isValidOrg) {
      isValidOrg = await this.handleOrgEligibilityForMultipleApps(
        grantProgramId,
        currentCycle.id,
        selectedOrg.document.id ? +selectedOrg.document.id : null,
        program.multipleApplicationsLimitType
      );
    }

    return {
      isValidOrg,
      errorMessage: !isValidOrg ?
        this.getMessageForIneligibleApplication(
          eligibilityCode,
          null,
          ApplicationEligibility.ApplicationEligibilityScenario.OrgForProgramRouting,
          isNomination,
          program.multipleApplicationsLimitType,
          program.multipleApplicationsLimitMessage
        ) :
        ''
    };
  }

  /**
   * Determines whether the routing org is eligible to proceed to the program
   *
   * @param selectedOrg: Selected Org
   * @param program: Program to route to
   * @param currentCycle: Current cycle of that program
   * @returns if the routing org is eligible to proceed to the program
   */
  async isProgramRoutingOrgEligible (
    selectedOrg: SearchResult,
    program: ProgramDetail,
    currentCycle: CyclesAPI.BaseProgramCycle
  ) {
    let isValidOrg = true;
    let eligibilityCode: ApplicationEligibility.OrganizationEligibilityCheckResponse;
    const passesLocationFiltering = this.countriesService.checkIfPassesLocationFiltering(
      selectedOrg.document.country,
      selectedOrg.document.stateProvRegName,
      program.allowedOrgLocations
    );
    if (passesLocationFiltering) {
      if (!!currentCycle) {
        if (!!program.charityBucketId) {
          const response = await this.handleCharityBucketScenario(
            selectedOrg,
            program.charityBucketId,
            program.clientId,
            eligibilityCode
          );
          isValidOrg = response.isValidOrg;
          eligibilityCode = response.eligibilityCode;
        } else {
          const regAuthInvalid = !this.nonprofitService.isRegAuthValidForProcessing(selectedOrg.document.registrationAuthority);
          if (regAuthInvalid) {
            isValidOrg = false;
            eligibilityCode = ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationNotValidForRegAuth;
          } else {
            const orgNotEligibleForYc = selectedOrg.document.eligibleForGivingStatusId !== OrganizationEligibleForGivingStatus.ELIGIBLE;
            if (!currentCycle.isClientProcessing && orgNotEligibleForYc) {
              const res = await this.captureVettingForOrg(
                selectedOrg,
                currentCycle,
                program.clientId,
                eligibilityCode
              );
              isValidOrg = res.isValidOrg;
              eligibilityCode = res.eligibilityCode;
            }
          }
        }
      }
    } else {
      isValidOrg = false;
      eligibilityCode = ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationLocationNotValid;
    }

    return {
      isValidOrg,
      eligibilityCode
    };
  }

  /**
   * When an org is not eligible for giving and the cycle is processed by YC
   * We collect vetting information and can proceed if it gets filled out
   *
   * @param selectedOrg: Selected Org
   * @param currentCycle: Current Cycle
   * @param clientId: Client ID
   * @param eligibilityCode: Eligibility Code
   * @returns if the org is valid and can proceed
   */
  async captureVettingForOrg (
    selectedOrg: SearchResult,
    currentCycle: CyclesAPI.BaseProgramCycle,
    clientId: number,
    eligibilityCode: ApplicationEligibility.OrganizationEligibilityCheckResponse
  ) {
    let isValidOrg = true;
    this.addOrganizationService.setSelectionType(AddOrgUI.SelectionType.UNVETTED_ORG);
    const adaptedSelectedOrg = this.nonprofitService.adaptNonprofitSearchResult(selectedOrg);
    this.addOrganizationService.setOriginalSearchResponse(adaptedSelectedOrg);
    this.spinnerService.stopSpinner();
    const processorType = currentCycle.isClientProcessing ? ProcessingTypes.Client : ProcessingTypes.YourCause;
    const response = await this.addOrganizationService.openConfirmVettingModal(
      adaptedSelectedOrg,
      clientId,
      processorType,
      []
    );
    if (!!response) {
      // Vetting was captured and is stored on the state
      isValidOrg = true;
    } else {
      isValidOrg = false;
      eligibilityCode = ApplicationEligibility.OrganizationEligibilityCheckResponse.VettingRequestAlreadyExists;
    }

    return {
      isValidOrg,
      eligibilityCode
    };
  }

  /**
   * Determines whether an org is valid for the charity bucket
   *
   * @param selectedOrg: Selected Org
   * @param charityBucketId: Charity Bucket ID
   * @param clientId: Client ID
   * @param eligibilityCode: Eligibility Code
   * @returns if the charity bucket org can proceed
   */
  async handleCharityBucketScenario (
    selectedOrg: SearchResult,
    charityBucketId: string,
    clientId: number,
    eligibilityCode: ApplicationEligibility.OrganizationEligibilityCheckResponse
  ) {
    const isValidOrg = await this.isOrgValidForCharityBucket(
      selectedOrg.document.registrationId,
      charityBucketId,
      clientId
    );
    if (!isValidOrg) {
      eligibilityCode = ApplicationEligibility.OrganizationEligibilityCheckResponse.OrganizationNotInCharityBucket;
    }

    return {
      isValidOrg,
      eligibilityCode
    };
  }

  /**
   * Determines whether the org is valid for the given charity bucket
   *
   * @param orgIdentification: Org Identification
   * @param charityBucketId: Charity Bucket ID
   * @param clientId: Client ID
   * @returns whether the org is valid for the given charity bucket
   */
  async isOrgValidForCharityBucket (
    orgIdentification: string,
    charityBucketId: string,
    clientId: number
  ): Promise<boolean> {
    const charityBucketSearchResponse = await this.nonprofitService.searchNonprofitsWithinBucket(
      orgIdentification,
      1,
      5,
      clientId,
      charityBucketId
    );

    return charityBucketSearchResponse?.data.some((org) => {
      const _orgIdentification = org.registrationNumber.replace('-', '');

      return _orgIdentification === orgIdentification;
    });
  }

  getOrgEligibilityMessageforMultipleApps (
    multipleApplicationsLimitType: CanOrgSubmitMultipleAppsOptions,
    multipleApplicationsLimitMessage: string,
    isNomination: boolean
  ) {
    switch (multipleApplicationsLimitType) {
      case CanOrgSubmitMultipleAppsOptions.CanApplyOncePerCycle:
        return multipleApplicationsLimitMessage || this.i18n.translate(
          isNomination ?
            'common:textCycleLimitedNominationMessage2' :
            'common:textCycleLimitedApplicationMessage2',
          {},
          isNomination ?
            'This program cycle is limited to one nomination per organization. Your organization has already been nominated in this cycle, and a new one cannot be submitted.' :
            'This program cycle is limited to one application per organization. Your organization has already applied in this cycle, and a new one cannot be submitted.'
        );
      case CanOrgSubmitMultipleAppsOptions.CanApplyOncePerProgram:
      case CanOrgSubmitMultipleAppsOptions.CanApplyIfPreviousCycleNotAwarded:
        return multipleApplicationsLimitMessage || this.i18n.translate(
          isNomination ?
            'common:textProgramLimitedNominationMessage' :
            'common:textProgramLimitedAppMessage',
          {},
          isNomination ?
            'This program is limited to one nomination per organization. Your organization has already been nominated, and a new one cannot be submitted.' :
            'This program is limited to one application per organization. Your organization has already applied, and a new one cannot be submitted.'
        );
    }

    return '';
  }

}
